import React, { useState, useEffect, useMemo } from 'react';
import { EyeIcon, PencilIcon } from '@heroicons/react/24/solid';
import ClubTermsModal from './ClubTermsModal';
import ClubTermsEditModal from './ClubTermsEditModal';
import ColorPicker, { TAILWIND_COLORS } from './ColorPicker';
import { useAuth } from '../context/AuthContext';
import { useClub } from '../context/ClubContext';
import {
  DEFAULT_MEMBERSHIP_HTML_FORM,
  interpolateMembershipForm,
  getMembershipFormPreviewVariables,
} from '../utils/membershipFormTemplate';
import { userTypeHasValidMembership } from '../utils/membershipFormData';
import {
  ONLINE_SIGNUP_BLOCKED_BY_PRIVILEGED_ACCESS_MESSAGE,
  userTypeHasPrivilegedClubAccess
} from '../utils/userTypePermissions';

const MembershipModal = ({ 
  isOpen, 
  onClose, 
  selectedUserType, 
  onUpdateUserType,
  inputClass, // Pass the common input class from parent
  accessControlType, // 'access_code' | 'shelly' | 'none' from the club's access control config
  courts = [],
  categories = []
}) => {
  const { user } = useAuth();
  const { clubDetails } = useClub();
  const [modalErrors, setModalErrors] = useState({});
  const [uiState, setUiState] = useState({
    isValid: false,
    term: '',
    fee: '',
    description: '',
    colour: 'slate',
    canRenewByZapper: false,
    zapperFee: '',
    canSignUpOnline: false,
    requiresApproval: false,
    allowAccess: false
  });
  const [htmlForm, setHtmlForm] = useState(DEFAULT_MEMBERSHIP_HTML_FORM);
  const [isFormPreviewOpen, setIsFormPreviewOpen] = useState(false);
  const [isFormEditOpen, setIsFormEditOpen] = useState(false);
  const [isColourPickerOpen, setIsColourPickerOpen] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  const accessControlEnabled = accessControlType === 'access_code' || accessControlType === 'shelly';
  const hasPrivilegedClubAccess = userTypeHasPrivilegedClubAccess(selectedUserType);
  const onlineSignupDisabled = hasPrivilegedClubAccess;

  const getDefaultMembershipColour = () => {
    const orderedCourts = [...(courts || [])].sort((a, b) => {
      const aIndex = Number(a?.sportIndex);
      const bIndex = Number(b?.sportIndex);
      if (!Number.isFinite(aIndex) && !Number.isFinite(bIndex)) return 0;
      if (!Number.isFinite(aIndex)) return 1;
      if (!Number.isFinite(bIndex)) return -1;
      return aIndex - bIndex;
    });
    const firstSport = String(orderedCourts[0]?.court || '').split('-')[0];
    const availableCategory = categories.find((category) =>
      category?.type === firstSport && category?.name === 'Available'
    );
    return availableCategory?.colour || 'slate';
  };

  // Convert backend data to UI state when modal opens (only once)
  useEffect(() => {
    if (!isInitialized && selectedUserType) {
      if (selectedUserType.membership) {
        const membership = selectedUserType.membership;
        setUiState({
          isValid: userTypeHasValidMembership(selectedUserType),
          term: membership.term || '',
          fee: membership.fee !== undefined && membership.fee !== null ? membership.fee : '',
          description: membership.description || '',
          colour: membership.colour || getDefaultMembershipColour(),
          canRenewByZapper: membership.zapperFee !== undefined,
          zapperFee: membership.zapperFee !== undefined && membership.zapperFee !== null ? membership.zapperFee : '',
          canSignUpOnline: membership.canSignUpOnline === true && !userTypeHasPrivilegedClubAccess(selectedUserType),
          requiresApproval: membership.requiresApproval === true,
          allowAccess: membership.allowAccess === true
        });
        setHtmlForm(selectedUserType.htmlForm || DEFAULT_MEMBERSHIP_HTML_FORM);
      } else {
        setUiState({
          isValid: false,
          term: '',
          fee: '',
          description: '',
          colour: getDefaultMembershipColour(),
          canRenewByZapper: false,
          zapperFee: '',
          canSignUpOnline: false,
          requiresApproval: false,
          allowAccess: false
        });
        setHtmlForm(selectedUserType.htmlForm || DEFAULT_MEMBERSHIP_HTML_FORM);
      }
      setIsInitialized(true);
    }
  }, [selectedUserType, isInitialized]);

  const handleUiStateChange = (field, value) => {
    setUiState(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const convertUiStateToBackendData = () => {
    const cleanedMembership = {};
    
    if (uiState.isValid) {
      // Only include term if it's a positive number
      if (uiState.term && uiState.term > 0) {
        cleanedMembership.term = uiState.term;
      }
      
      // Only include fee if it's 0 or positive
      if (uiState.fee !== '' && uiState.fee !== null && uiState.fee >= 0) {
        cleanedMembership.fee = uiState.fee;
      }

      if (uiState.description.trim()) {
        cleanedMembership.description = uiState.description.trim();
      }

      cleanedMembership.colour = uiState.colour || getDefaultMembershipColour();
      
      // Only include zapperFee if canRenewByZapper is true and fee is 0 or positive
      if (uiState.canRenewByZapper && uiState.zapperFee !== '' && uiState.zapperFee !== null && uiState.zapperFee >= 0) {
        cleanedMembership.zapperFee = uiState.zapperFee;
      }

      if (uiState.canSignUpOnline && !onlineSignupDisabled) {
        cleanedMembership.canSignUpOnline = true;

        if (uiState.requiresApproval) {
          cleanedMembership.requiresApproval = true;
        }
      }
    }

    // allowAccess applies independently of a valid membership (e.g. Super Users with non-expiring
    // permissions still need permanent club access), so save it regardless of uiState.isValid.
    if (accessControlEnabled && uiState.allowAccess) {
      cleanedMembership.allowAccess = true;
    }

    return cleanedMembership;
  };

  const handleModalMembershipChange = (field, value) => {
    if (!selectedUserType) return;

    if (field === 'canSignUpOnline' && value && onlineSignupDisabled) {
      return;
    }

    if (field === 'canSignUpOnline' && !value) {
      setUiState((prev) => ({
        ...prev,
        canSignUpOnline: false,
        requiresApproval: false
      }));
    } else {
      handleUiStateChange(field, value);
    }

    console.log('[MembershipModal] handleModalMembershipChange', { field, value });
  };

  const previewHtml = useMemo(() => (
    interpolateMembershipForm(
      htmlForm,
      getMembershipFormPreviewVariables(user, {
        userTypeName: selectedUserType?.name,
        clubName: clubDetails?.name,
      })
    )
  ), [htmlForm, selectedUserType?.name, user, clubDetails?.name]);

  if (!isOpen || !selectedUserType) return null;

  // Validation
  const validateMembershipModal = () => {
    const errors = {};
    if (uiState.isValid) {
      // Membership term must be a positive integer (not 0, not empty)
      if (uiState.term === '' || isNaN(uiState.term) || uiState.term <= 0) {
        errors.term = 'Membership term must be a positive integer';
      }
      // Membership fee can be 0 or positive integer
      if (uiState.fee === '' || isNaN(uiState.fee) || uiState.fee < 0) {
        errors.fee = 'Membership fee must be 0 or a positive integer';
      }
      // Zapper fee can be 0 or positive integer (only if canRenewByZapper is checked)
      if (uiState.canRenewByZapper && (uiState.zapperFee === '' || isNaN(uiState.zapperFee) || uiState.zapperFee < 0)) {
        errors.zapperFee = 'Payment fee must be 0 or a positive integer';
      }
    }
    setModalErrors(errors);
    console.log('[MembershipModal] validateMembershipModal', { errors });
    return Object.keys(errors).length === 0;
  };

  const handleCloseModal = () => {
    const valid = validateMembershipModal();
    console.log('[MembershipModal] handleCloseModal', { valid, modalErrors });
    if (valid) {
      // Convert final UI state to backend data and save
      const cleanedMembership = convertUiStateToBackendData();
      
      const updatedUserType = {
        ...selectedUserType,
        membership: cleanedMembership,
        htmlForm: uiState.canSignUpOnline ? htmlForm : ''
      };
      
      console.log('[MembershipModal] handleCloseModal - saving final state', { updatedUserType });
      onUpdateUserType(updatedUserType);
      
      setModalErrors({});
      setIsInitialized(false); // Reset for next modal open
      onClose();
    }
  };

  return (
    <>
      <div
        className="fixed inset-0 z-50 overflow-y-auto overscroll-contain bg-slate-500/75"
        role="dialog"
        aria-modal="true"
        aria-labelledby="membership-modal-title"
      >
        <div className="flex min-h-screen w-full items-center justify-center p-3 py-8 sm:p-4 sm:py-10">
          <div className="relative w-full max-w-2xl bg-white rounded-lg shadow-xl">
          <div className="p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 id="membership-modal-title" className="text-lg font-medium text-slate-800 pr-2">
                Membership Configuration for {selectedUserType.name}
              </h3>
              <button
                type="button"
                onClick={() => {
                  setIsInitialized(false); // Reset for next modal open
                  onClose();
                }}
                className="text-slate-600 hover:text-slate-600"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-6">
            {/* Is valid membership */}
            <label className="flex items-center">
               <input
                 type="checkbox"
                 checked={uiState.isValid}
                 onChange={(e) => handleModalMembershipChange('isValid', e.target.checked)}
                 className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
               />
              <span className="ml-2 text-sm text-slate-600">Is valid membership</span>
            </label>

            {uiState.isValid && (
              <div className="ml-6 space-y-4">
                <div className={`rounded-lg border-2 bg-white p-3 ${TAILWIND_COLORS[uiState.colour]?.border || 'border-slate-300'}`}>
                  <textarea
                    value={uiState.description}
                    onChange={(e) => handleModalMembershipChange('description', e.target.value)}
                    className="w-full min-h-[110px] resize-y border-0 bg-transparent p-0 text-sm text-slate-700 placeholder:text-slate-400 focus:border-0 focus:outline-none focus:ring-0"
                    rows={4}
                    maxLength={300}
                    placeholder="Add a description to show this membership card on the Memberships page..."
                  />
                  <div className="mt-3 flex items-center justify-end border-t border-slate-100 pt-3">
                    <div className="w-32">
                      <ColorPicker
                        value={TAILWIND_COLORS[uiState.colour] || TAILWIND_COLORS.slate}
                        onChange={(colorValue) => handleModalMembershipChange('colour', colorValue.colorName)}
                        isOpen={isColourPickerOpen}
                        onToggle={() => setIsColourPickerOpen((open) => !open)}
                      />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 items-center">
                  <label className="block text-sm text-slate-600">Membership term in months</label>
                  <div>
                    <input
                      type="number"
                      value={uiState.term}
                      onChange={(e) => {
                        const value = e.target.value === '' ? '' : parseInt(e.target.value);
                        handleModalMembershipChange('term', value);
                      }}
                      className={`${inputClass} ${modalErrors.term ? 'border-red-500' : ''}`}
                      min="1"
                    />
                    {modalErrors.term && (
                      <p className="mt-1 text-xs text-red-500">{modalErrors.term}</p>
                    )}
                  </div>
                  <label className="block text-sm text-slate-600">Membership term fee</label>
                  <div>
                    <input
                      type="number"
                      value={uiState.fee}
                      onChange={(e) => {
                        const value = e.target.value === '' ? '' : parseInt(e.target.value);
                        handleModalMembershipChange('fee', value);
                      }}
                      className={`${inputClass} ${modalErrors.fee ? 'border-red-500' : ''}`}
                      min="0"
                    />
                    {modalErrors.fee && (
                      <p className="mt-1 text-xs text-red-500">{modalErrors.fee}</p>
                    )}
                  </div>
                </div>

                {/* Online payment for renewal and signup (when signup is enabled) */}
                {uiState.fee >= 0 && (
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={uiState.canRenewByZapper}
                      onChange={(e) => handleModalMembershipChange('canRenewByZapper', e.target.checked)}
                      className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm text-slate-600">Can pay online for renewal or sign up (if enabled)</span>
                  </label>
                )}

                {uiState.canRenewByZapper && (
                  <div className="grid grid-cols-2 gap-4 items-center ml-6">
                    <label className="block text-sm text-slate-600">Online payment fee</label>
                    <div>
                      <input
                        type="number"
                        value={uiState.zapperFee}
                        onChange={(e) => {
                          const value = e.target.value === '' ? '' : parseInt(e.target.value);
                          handleModalMembershipChange('zapperFee', value);
                        }}
                        className={`${inputClass} ${modalErrors.zapperFee ? 'border-red-500' : ''}`}
                        min="0"
                      />
                      {modalErrors.zapperFee && (
                        <p className="mt-1 text-xs text-red-500">{modalErrors.zapperFee}</p>
                      )}
                    </div>
                  </div>
                )}

                <div className="flex flex-col gap-1">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={uiState.canSignUpOnline}
                      onChange={(e) => handleModalMembershipChange('canSignUpOnline', e.target.checked)}
                      disabled={onlineSignupDisabled}
                      className="rounded border-slate-300 text-blue-600 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-50"
                    />
                    <span className={`ml-2 text-sm ${onlineSignupDisabled ? 'text-slate-400' : 'text-slate-600'}`}>
                      Can sign up online
                    </span>
                  </label>
                  {onlineSignupDisabled && (
                    <p className="text-xs text-slate-500">
                      {ONLINE_SIGNUP_BLOCKED_BY_PRIVILEGED_ACCESS_MESSAGE}
                    </p>
                  )}
                </div>
                {uiState.canSignUpOnline && (
                  <div className="flex flex-wrap items-center gap-3">
                    <div className="ml-auto flex gap-2">
                      <button
                        type="button"
                        onClick={() => setIsFormPreviewOpen(true)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-slate-300 shadow-sm text-xs font-medium rounded text-slate-600 bg-white hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                        title="Preview membership signup form"
                      >
                        <EyeIcon className="h-4 w-4" />
                        Preview
                      </button>
                      <button
                        type="button"
                        onClick={() => setIsFormEditOpen(true)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-slate-300 shadow-sm text-xs font-medium rounded text-slate-600 bg-white hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                        title="Edit membership signup form"
                      >
                        <PencilIcon className="h-4 w-4" />
                        Edit
                      </button>
                    </div>
                  </div>
                )}

                {uiState.canSignUpOnline && (
                  <div className="ml-6">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={uiState.requiresApproval}
                        onChange={(e) => handleModalMembershipChange('requiresApproval', e.target.checked)}
                        className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="ml-2 text-sm text-slate-600">Requires Admin approval</span>
                    </label>
                    <p className="mt-1 ml-6 text-xs text-slate-500">
                      Applications must be approved in Club Admin before the member can pay or activate.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Allow club access at all times — shown whenever the club has access control configured,
                independently of whether this user type has a valid membership (e.g. Super Users). */}
            {accessControlEnabled && (
              <label className="flex items-start">
                <input
                  type="checkbox"
                  checked={uiState.allowAccess}
                  onChange={(e) => handleModalMembershipChange('allowAccess', e.target.checked)}
                  className="mt-0.5 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-slate-600">Allow club access at all times from the sidebar menu</span>
              </label>
            )}
            </div>

            <div className="mt-6 flex justify-end">
              <button
                type="button"
                onClick={handleCloseModal}
                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      </div>
      </div>

      <ClubTermsModal
        isOpen={isFormPreviewOpen}
        onClose={() => setIsFormPreviewOpen(false)}
        htmlContent={previewHtml}
        membershipTypeName={selectedUserType?.name}
        membershipColour={uiState.colour}
      />

      <ClubTermsEditModal
        isOpen={isFormEditOpen}
        onClose={() => setIsFormEditOpen(false)}
        htmlContent={htmlForm}
        onSave={(updatedHtml) => {
          setHtmlForm(updatedHtml);
          setIsFormEditOpen(false);
        }}
        title="Edit Membership Signup Form"
        description={'Customize the HTML shown to members before signup. Use {{firstName}}, {{lastName}}, {{userTypeName}}, and {{clubName}} for values filled in automatically from the logged-in user, selected membership, and club details. Add input fields as needed and mark any mandatory field with the HTML required attribute, for example: <input name="emergencyContactName" required />.'}
      />
    </>
  );
};

export default MembershipModal;
